import React, { useCallback, useEffect, useRef, useState } from "react";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { User } from "@scrile/api-provider/dist/api/UserProvider";
import { useTranslation } from "locales";
import emitter, { EVENTS } from "lib/emitter";
import { getMessageMetadata, stripMetaPrefix } from "lib/messageMetadata";
import providers from "lib/providers";
import BaseAvatar from "components/BaseAvatar";
import MessageBubble from "components/MessageBubble";
import "./style.scss";

interface Props {
  parentMessage: Message;
  authUser: User;
  threadId: string;
  onClose: () => void;
}

export default function ThreadPanel({ parentMessage, authUser, threadId, onClose }: Props) {
  const { t } = useTranslation();
  const [replies, setReplies] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const loadReplies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await providers.MessagesProvider.find({
        data: { threadId, sort: "TIME,ASC", page: 0, size: 200 },
      });
      if (!mountedRef.current) return;
      const allMessages = response.result || [];
      const threadReplies = allMessages.filter((msg: any) => {
        const meta = getMessageMetadata(msg);
        return meta?.threadParentId === parentMessage.id;
      });
      setReplies(threadReplies);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[ThreadPanel] Failed to load replies:", e);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [threadId, parentMessage.id]);

  useEffect(() => {
    loadReplies();
  }, [loadReplies]);

  // Listen for new messages that might be thread replies. Scope by BOTH the
  // parent id AND this panel's thread id — without the threadId guard a reply
  // in a same-parent-id thread of another chat could leak in.
  useEffect(() => {
    const handleNewMessage = (msg: Message) => {
      const meta = getMessageMetadata(msg);
      if (meta?.threadParentId === parentMessage.id && String(msg.threadId) === String(threadId)) {
        setReplies((prev) => {
          if (prev.some((r) => r.id === msg.id)) return prev;
          return [...prev, msg];
        });
      }
    };
    emitter.on(EVENTS.MESSAGES_NEW_MESSAGE, handleNewMessage);
    return () => {
      emitter.off(EVENTS.MESSAGES_NEW_MESSAGE, handleNewMessage);
    };
  }, [parentMessage.id, threadId]);

  // Refocus the reply input after it re-enables. Calling focus() in handleSend
  // ran while the textarea was still `disabled={sending}` (focus on a disabled
  // element is a no-op), so focus fell back to the main chat input.
  const justSentRef = useRef(false);
  useEffect(() => {
    if (!sending && justSentRef.current) {
      justSentRef.current = false;
      inputRef.current?.focus();
    }
  }, [sending]);

  // Auto-scroll to bottom on new replies AND once the initial load renders.
  // While `loading` is true the reply list isn't in the DOM yet, so a pass
  // triggered by `replies.length` alone measured an empty scroller and the
  // panel opened at the top.
  useEffect(() => {
    if (loading) return;
    const el = scrollRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [replies.length, loading]);

  const handleSend = useCallback(async () => {
    const text = inputValue.trim();
    if (!text || sending) return;
    // Can't reply into a thread whose parent was deleted for everyone.
    if (getMessageMetadata(parentMessage)?.deletedForAll) return;

    setSending(true);
    try {
      const meta = {
        threadParentId: parentMessage.id,
        replyToMessageId: parentMessage.id,
        replyToText: stripMetaPrefix(parentMessage.text).substring(0, 100),
        replyToSenderId: parentMessage.senderId,
        replyToSenderName: (parentMessage as any).senderUser?.screenName || "",
      };
      const metaPrefix = `__META__:${JSON.stringify(meta)}\n`;
      const result = await providers.MessagesProvider.sendExtendedMessages({
        data: {
          text: metaPrefix + text,
          threadIds: [threadId],
        },
      });
      // Optimistically append locally instead of re-emitting on the GLOBAL
      // MESSAGES_NEW_MESSAGE bus: the sent reply already reaches every surface
      // (main list + this panel's listener) through the live subscription, so
      // the manual emit only double-delivered and bumped unread.
      if (result && result.length > 0 && mountedRef.current) {
        setReplies((prev) => {
          const next = [...prev];
          for (const msg of result as Message[]) {
            if (!next.some((r) => r.id === msg.id)) next.push(msg);
          }
          return next;
        });
      }
      if (!mountedRef.current) return;
      setInputValue("");
      justSentRef.current = true;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[ThreadPanel] Failed to send reply:", e);
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [inputValue, sending, parentMessage, threadId]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
      if (e.key === "Escape") {
        onClose();
      }
    },
    [handleSend, onClose]
  );

  const parentCleanText = stripMetaPrefix(parentMessage.text);

  return (
    <div className="thread-panel">
      <div className="thread-panel__header">
        <div className="thread-panel__header-title">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"
              fill="currentColor"
            />
          </svg>
          {t("Thread")}
        </div>
        <button className="thread-panel__close" onClick={onClose} type="button">
          <svg viewBox="0 0 24 24" width="18" height="18">
            <path
              d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"
              fill="currentColor"
            />
          </svg>
        </button>
      </div>

      {/* Parent message */}
      <div className="thread-panel__parent">
        <div className="thread-panel__parent-avatar">
          <BaseAvatar user={(parentMessage as any).senderUser} size={32} />
        </div>
        <div className="thread-panel__parent-body">
          <div className="thread-panel__parent-name">{(parentMessage as any).senderUser?.screenName || t("User")}</div>
          <div className="thread-panel__parent-text">{parentCleanText}</div>
        </div>
      </div>

      <div className="thread-panel__divider">
        {replies.length > 0
          ? `${replies.length} ${replies.length === 1 ? t("reply") : t("replies")}`
          : t("No replies yet")}
      </div>

      {/* Replies */}
      <div className="thread-panel__replies" ref={scrollRef}>
        {loading && <div className="thread-panel__loading">{t("Loading...")}</div>}
        {!loading &&
          replies.map((reply) => (
            <MessageBubble
              key={reply.id}
              message={reply}
              authUser={authUser}
              isOwn={reply.senderId === authUser.id}
              showAvatar={true}
              showName={true}
              isStart={true}
              isEnd={true}
            />
          ))}
      </div>

      {/* Input */}
      <div className="thread-panel__input">
        <textarea
          ref={inputRef}
          className="thread-panel__textarea"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={t("Reply in thread...")}
          rows={1}
          disabled={sending}
        />
        <button
          className="thread-panel__send"
          onClick={handleSend}
          disabled={!inputValue.trim() || sending}
          type="button"
        >
          <svg viewBox="0 0 24 24" width="20" height="20">
            <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" fill="currentColor" />
          </svg>
        </button>
      </div>
    </div>
  );
}
